import React from "react";

export interface SpinnerProps {
    size?: number;
    accentColor?: string;
    ariaLabel?: string;
    delayMs?: number;
    blockInteraction?: boolean;
}

export interface SpinnerState {
    angle: number;
    dash: { a: number; o: number };
    reduceMotion: boolean;
    show: boolean;
}

export default class Spinner extends React.PureComponent<SpinnerProps, SpinnerState> {
    static defaultProps: Required<Pick<SpinnerProps, "size" | "accentColor" | "ariaLabel" | "delayMs" | "blockInteraction">> = {
        size: 72,
        accentColor: "#c79656",
        ariaLabel: "Loading…",
        delayMs: 100,
        blockInteraction: true,
    };

    private rafId: number | null = null;
    private startTs = 0;
    private mql: MediaQueryList | null = null;
    private delayId: number | null = null;

    constructor(props: SpinnerProps) {
        super(props);
        this.state = { angle: 0, dash: { a: 1, o: 0 }, reduceMotion: false, show: false };
    }

    private boundChange = (e: MediaQueryListEvent | MediaQueryList) => {
        const matches = "matches" in e ? e.matches : (e as MediaQueryList).matches;
        this.setState({ reduceMotion: matches }, () => {
            if (this.state.reduceMotion) {
                if (this.rafId) cancelAnimationFrame(this.rafId);
                this.rafId = null;
            } else if (!this.rafId) {
                this.startTs = performance.now();
                this.loop(this.startTs);
            }
        });
    };

    componentDidMount() {
        this.mql = typeof window !== "undefined" && window.matchMedia
            ? window.matchMedia("(prefers-reduced-motion: reduce)")
            : null;

        const reduceMotion = !!this.mql?.matches;
        this.setState({ reduceMotion }, () => {
            if (!this.state.reduceMotion) {
                this.startTs = performance.now();
                this.loop(this.startTs);
            }
        });

        if (this.mql) {
            if (typeof this.mql.addEventListener === "function") {
                this.mql.addEventListener("change", this.boundChange as EventListener);
            } else {
                (this.mql as any).onchange = this.boundChange as any;
            }
        }

        this.delayId = window.setTimeout(() => this.setState({ show: true }), this.props.delayMs);
    }

    componentWillUnmount() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        if (this.delayId) clearTimeout(this.delayId);
        if (this.mql) {
            if (typeof this.mql.removeEventListener === "function") {
                this.mql.removeEventListener("change", this.boundChange as EventListener);
            } else if ("onchange" in this.mql) {
                (this.mql as any).onchange = null;
            }
        }
    }

    private loop = (t: number) => {
        const rotPeriod = 1150;
        const angle = (((t - this.startTs) % rotPeriod) / rotPeriod) * 360;
        const dashPeriod = 1600;
        const tt = ((t - this.startTs) % dashPeriod) / dashPeriod;
        const ease = tt < 0.5 ? 2 * tt * tt : -1 + (4 - 2 * tt) * tt;
        const c = 2 * Math.PI * 42;
        const a = 1 + (140 - 1) * ease;
        const o = -tt * c;
        this.setState({ angle, dash: { a, o } });
        this.rafId = requestAnimationFrame(this.loop);
    };

    render() {
        const { size, accentColor, ariaLabel, blockInteraction } = this.props;
        const { angle, dash, reduceMotion, show } = this.state;

        if (!show) return null;

        const radius = 42;
        const stroke = Math.max(6, Math.round((size as number) / 12));
        const c = 2 * Math.PI * radius;

        const overlay: React.CSSProperties = {
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            background: "#FFFFFF",           // opaque background covering the parent
            zIndex: 10,
            pointerEvents: blockInteraction ? "auto" : "none",
            borderRadius: 12,                // optional if parent has rounding; remove if not desired
        };

        const svgStyle: React.CSSProperties = {
            display: "block",
            width: size,
            height: size,
            filter: [
                "drop-shadow(0 2px 10px rgba(199,150,86,0.35))",
                "drop-shadow(0 8px 28px rgba(199,150,86,0.25))",
            ].join(" "),
            zIndex: 11,
        };

        const trackStyle: React.CSSProperties = {
            fill: "none",
            stroke: "rgba(199,150,86,0.12)",
            strokeWidth: stroke,
        };

        const indicatorStyle: React.CSSProperties = {
            fill: "none",
            stroke: accentColor,
            strokeWidth: stroke,
            strokeLinecap: "round",
            transformOrigin: "50% 50%",
            transform: reduceMotion ? undefined : `rotate(${angle}deg)`,
            strokeDasharray: `${reduceMotion ? c : dash.a} ${c}`,
            strokeDashoffset: reduceMotion ? 0 : dash.o,
            filter: "drop-shadow(0 0 14px rgba(199,150,86,0.55))",
        };

        return (
            <div role="status" aria-live="polite" aria-label={ariaLabel} style={overlay}>
                <svg viewBox="0 0 100 100" width={size} height={size} style={svgStyle} aria-hidden="true">
                    <circle cx={50} cy={50} r={radius} style={trackStyle} />
                    <circle cx={50} cy={50} r={radius} style={indicatorStyle} />
                </svg>
            </div>
        );
    }
}
